import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  Loader2, 
  X, 
  FileText, 
  Image as ImageIcon,
  Video, 
  Heart,
  Edit,
  ThumbsDown,
  RefreshCw,
  Check,
  CheckCircle,
  ExternalLink,
  AlertTriangle,
  Globe,
  Layers,
  Type,
  Camera,
  ChevronDown
} from 'lucide-react';
import { 
  FaLinkedin, FaInstagram, 
  FaTwitter, FaPinterest, FaBlog, FaEnvelope, FaNewspaper, FaFacebook, FaMedium, FaYoutube
} from 'react-icons/fa';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';

// Platform configurations matching backend PLATFORM_SETTINGS
const platforms = [
  { key: 'x', label: 'X', icon: FaTwitter, color: '#000000', maxLength: 280, hasHeadline: false, hashtags: 3, image: '1200×675 (16:9)' },
  { key: 'linkedin', label: 'LinkedIn Beitrag', icon: FaLinkedin, color: '#0077B5', maxLength: 3000, hasHeadline: false, hashtags: 5, image: '1200×627 (1.91:1)' },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E4405F', maxLength: 300, hasHeadline: false, hashtags: 5, image: '1080×1350 (4:5)' },
  { key: 'facebook', label: 'Facebook', icon: FaFacebook, color: '#1877F2', maxLength: 5000, hasHeadline: false, hashtags: 3, image: '1200×630 (1.91:1)' },
  { key: 'pinterest', label: 'Pinterest', icon: FaPinterest, color: '#BD081C', maxLength: 320, hasHeadline: true, headlineMax: 28, hashtags: 5, image: '1000×1500 (2:3)' },
  { key: 'medium', label: 'Medium', icon: FaMedium, color: '#000000', maxLength: 4000, hasHeadline: true, headlineMax: 67, hashtags: 0, image: '1200×628 (1.91:1)' },
  { key: 'blog', label: 'Blogartikel', icon: FaBlog, color: '#00D9C8', maxLength: 2800, hasHeadline: true, headlineMax: 60, hashtags: 0, image: '1200×628 (1.91:1)' },
  { key: 'newsletter', label: 'Newsletter', icon: FaEnvelope, color: '#F59E0B', maxLength: 600, hasHeadline: true, headlineMax: 70, hashtags: 0, image: null },
  { key: 'pressemitteilung', label: 'Pressemitteilung', icon: FaNewspaper, color: '#6B7280', maxLength: 5000, hasHeadline: true, headlineMax: 57, hashtags: 0, image: '1200×628 (1.91:1)' },
  { key: 'youtube', label: 'YouTube', icon: FaYoutube, color: '#FF0000', maxLength: 450, hasHeadline: true, headlineMax: 100, hashtags: 0, image: '1920×1080 (16:9)', isVideo: true },
];

const LANGUAGE_OPTIONS = [
  { code: 'de', label: 'Deutsch', abbr: 'DE' },
  { code: 'en', label: 'English', abbr: 'EN' },
  { code: 'pl', label: 'Polski', abbr: 'PL' },
  { code: 'fr', label: 'Français', abbr: 'FR' },
  { code: 'es', label: 'Español', abbr: 'ES' },
  { code: 'it', label: 'Italiano', abbr: 'IT' },
  { code: 'nl', label: 'Nederlands', abbr: 'NL' },
  { code: 'pt', label: 'Português', abbr: 'PT' },
  { code: 'cs', label: 'Čeština', abbr: 'CS' },
  { code: 'hu', label: 'Magyar', abbr: 'HU' },
  { code: 'sv', label: 'Svenska', abbr: 'SV' },
  { code: 'tr', label: 'Türkçe', abbr: 'TR' },
  { code: 'ro', label: 'Română', abbr: 'RO' },
  { code: 'lv', label: 'Latviešu', abbr: 'LV' },
];

const ELEMENT_OPTIONS = [
  { code: 'hauptprodukt', label: 'Hauptprodukt' },
  { code: 'markengesicht', label: 'Markengesicht' },
];

// Sprachabhängige Default-Demo-Ideen
const getDefaultIdeas = (currentLang) => {
  if (currentLang === 'en') {
    return [
      {
        id: 'demo-1',
        title: '5 Ways AI Cuts Your Content Costs by 70%',
        description: 'Detailed comparison between traditional agencies and AI-powered content solutions. Shows concrete savings potential.',
        platform: 'blog',
        goal: 'Lead Generation',
        language: 'en',
      },
      {
        id: 'demo-2',
        title: 'The Hidden ROI of Content Automation',
        description: 'LinkedIn post about the true costs of manual content creation vs. automated multi-channel content distribution.',
        platform: 'linkedin',
        goal: 'Awareness',
        language: 'en',
      },
      {
        id: 'demo-3',
        title: 'From 1 to 10: Content Repurposing in Action',
        description: 'Visual carousel post showing how a single content input transforms into 10 different formats.',
        platform: 'instagram',
        goal: 'Engagement',
        language: 'en',
      },
      {
        id: 'demo-4',
        title: 'Case Study: How a Startup Saves 80% Marketing Time',
        description: 'Success story of an SMB that tripled its marketing efficiency through AI-powered content transformation.',
        platform: 'medium',
        goal: 'Lead Generation',
        language: 'en',
      },
    ];
  }
  return [
    {
      id: 'demo-1',
      title: '5 Wege, wie KI Ihre Content-Kosten um 70% senkt',
      description: 'Detaillierter Vergleich zwischen traditionellen Agenturen und KI-gestützten Content-Lösungen. Zeigt konkrete Einsparpotenziale auf.',
      platform: 'blog',
      goal: 'Lead Generation',
      language: 'de',
    },
    {
      id: 'demo-2',
      title: 'Der versteckte ROI von Content-Automatisierung',
      description: 'LinkedIn-Post über die wahren Kosten manueller Content-Erstellung vs. automatisierte Multi-Channel Content-Distribution.',
      platform: 'linkedin',
      goal: 'Awareness',
      language: 'de',
    },
    {
      id: 'demo-3',
      title: 'Aus 1 mach 10: Content Repurposing in Aktion',
      description: 'Visueller Carousel-Post, der zeigt, wie ein einzelner Content-Input in 10 verschiedene Formate transformiert wird.',
      platform: 'instagram',
      goal: 'Engagement',
      language: 'de',
    },
    {
      id: 'demo-4',
      title: 'Case Study: Wie ein Startup 80% Marketing-Zeit spart',
      description: 'Erfolgsgeschichte eines KMU, das durch KI-gestützte Content-Transformation seine Marketing-Effizienz verdreifacht hat.',
      platform: 'medium',
      goal: 'Lead Generation',
      language: 'de',
    },
  ];
};

// Sprachabhängige Refresh-Ideen
const getRefreshIdeas = (currentLang) => {
  const stamp = Date.now();
  if (currentLang === 'en') {
    return [
      {
        id: `refresh-${stamp}-1`,
        title: 'Why SMBs Should Embrace AI Content Now',
        description: 'Analysis of current market dynamics and why companies without AI-powered content marketing risk falling behind.',
        platform: 'linkedin',
        goal: 'Awareness',
        language: 'en',
      },
      {
        id: `refresh-${stamp}-2`,
        title: 'Auto-Fill Your Content Calendar',
        description: 'How-to post showing how xchannels.ai builds a complete monthly content plan in 10 minutes.',
        platform: 'instagram',
        goal: 'Engagement',
        language: 'en',
      },
    ];
  }
  return [
    {
      id: `refresh-${stamp}-1`,
      title: 'Warum KMUs jetzt auf KI-Content setzen sollten',
      description: 'Analyse der aktuellen Marktentwicklung und warum Unternehmen ohne KI-gestütztes Content-Marketing den Anschluss verlieren.',
      platform: 'linkedin',
      goal: 'Awareness',
      language: 'de',
    },
    {
      id: `refresh-${stamp}-2`,
      title: 'Content-Kalender automatisch befüllen',
      description: 'How-To Post der zeigt, wie xchannels.ai einen kompletten Monats-Content-Plan in 10 Minuten erstellt.',
      platform: 'instagram',
      goal: 'Engagement',
      language: 'de',
    },
  ];
};

const ShowcaseKreation = ({ sessionId, language, onViewInProduction }) => {
  const { t, lang } = useLanguage();
  const isEn = lang === 'en';
  const [description, setDescription] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [files, setFiles] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState([]);
  const [contentCount, setContentCount] = useState(82);
  const [ideas, setIdeas] = useState(() => getDefaultIdeas(lang));
  const [isLoadingIdeas, setIsLoadingIdeas] = useState(false);
  const [editingIdeaId, setEditingIdeaId] = useState(null);
  const [editedIdeaText, setEditedIdeaText] = useState('');
  const [userChannels, setUserChannels] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);
  // Showcase mock — update if plan defaults change
  const [videoLimit, setVideoLimit] = useState({ used: 0, limit: 1, remaining: 1 });
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showElementDropdown, setShowElementDropdown] = useState(false);
  const [keepInputText, setKeepInputText] = useState(false);
  const [keepUploadedPhotos, setKeepUploadedPhotos] = useState(false);
  const pollingIntervalRef = useRef(null);
  const [enabledPlatforms, setEnabledPlatforms] = useState(null); // null = not loaded yet
  const [customChannels, setCustomChannels] = useState([]);
  const [showInstagramBanner, setShowInstagramBanner] = useState(() => {
    return !localStorage.getItem('instagram_hashtag_banner_dismissed');
  });

  // Re-generate Demo-Ideen wenn die Sprache wechselt
  useEffect(() => {
    setIdeas(getDefaultIdeas(lang));
    setSelectedLanguage(lang === 'en' ? 'en' : 'de');
  }, [lang]);

  // Filter platforms based on user's enabled platforms config
  const activePlatforms = (() => {
    if (enabledPlatforms === null || enabledPlatforms.length === 0) return platforms; // Show all if not configured
    const filtered = platforms.filter(p => enabledPlatforms.includes(p.key));
    // Add custom channels as platform options
    customChannels.filter(c => enabledPlatforms.includes(c.id)).forEach(c => {
      const mainField = c.fields?.[0];
      filtered.push({
        key: c.id, label: c.name, icon: Layers, color: '#A0AEC0',
        maxLength: mainField?.maxLength || 500, hasHeadline: false, hashtags: 0, image: null
      });
    });
    return filtered;
  })();

  const MAX_PLATFORMS = 5; // Maximum platforms at once for reliable generation
  
  const togglePlatform = (key) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(key)) {
        return prev.filter(p => p !== key);
      }
      // Check YouTube video limit
      if (key === 'youtube') {
        if (videoLimit.remaining <= 0) {
          toast.error((t.kreation.videoLimit || '').replace('{limit}', String(videoLimit.limit)));
          return prev;
        }
        toast.info(`Sie haben noch ${videoLimit.remaining}/${videoLimit.limit} Video${videoLimit.remaining === 1 ? "" : "s"} übrig diesen Monat`);
      }
      // Check max limit
      if (prev.length >= MAX_PLATFORMS) {
        toast.warning(`Maximal ${MAX_PLATFORMS} Plattformen gleichzeitig auswählbar für zuverlässige Generierung`);
        return prev;
      }
      return [...prev, key];
    });
  };

  const handleFileUpload = async (uploadFiles) => {
    for (const file of uploadFiles) {
      const fileId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setFiles(prev => [...prev, {
        id: fileId,
        name: file.name,
        type: file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : 'document',
        size: file.size,
        file: file
      }]);
    }
  };

  const removeFile = (fileId) => {
    setFiles(prev => {
      const next = prev.filter(f => f.id !== fileId);
      // Reset toggles if no images remain
      if (!next.some(f => f.type === 'image')) {
        setKeepInputText(false);
        setKeepUploadedPhotos(false);
      }
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error(t.kreation.describeRequired || (isEn ? 'Please enter a topic' : 'Bitte Thema eingeben'));
      return;
    }
    if (selectedPlatforms.length === 0) {
      toast.error(t.kreation.platformRequired || (isEn ? 'Please select a platform' : 'Bitte Plattform wählen'));
      return;
    }

    setIsGenerating(true);
    setGeneratedContent([]);

    // Simulate generation delay
    setTimeout(() => {
      const mockPieces = selectedPlatforms.map((platformKey, idx) => {
        const platform = platforms.find(p => p.key === platformKey);

        // Realistic demo text per platform — produces visible content cards
        const demoTexts = {
          linkedin: isEn
            ? `${description}\n\nThis is what modern content marketing looks like. AI-powered, multi-channel, on-brand.\n\n#contentmarketing #ai #marketing`
            : `${description}\n\nSo sieht modernes Content Marketing aus. KI-gestützt, multi-channel, on-brand.\n\n#contentmarketing #ki #marketing`,
          instagram: isEn
            ? `${description} ✨\n\nFrom idea to post in seconds — that's the power of AI content creation.\n\n#contentcreation #aimarketing #socialmedia #digital #startup`
            : `${description} ✨\n\nVon der Idee zum Post in Sekunden — das ist die Power von KI Content Creation.\n\n#contentcreation #kimarketing #socialmedia #digital #startup`,
          blog: isEn
            ? `# ${description}\n\nThe digital landscape is changing faster than ever. Companies that don't adapt their content strategy now risk falling behind.\n\n## Why This Matters\n\nContent marketing has evolved from a nice-to-have to an absolute necessity.`
            : `# ${description}\n\nDie digitale Landschaft verändert sich schneller als je zuvor. Unternehmen die ihre Content-Strategie jetzt nicht anpassen, riskieren den Anschluss zu verlieren.\n\n## Warum das wichtig ist\n\nContent Marketing hat sich von einem Nice-to-have zu einer absoluten Notwendigkeit entwickelt.`,
          newsletter: isEn
            ? `Subject: ${description}\n\nDear Reader,\n\nThis week we're diving deep into a topic that affects every marketing team: ${description.toLowerCase()}.`
            : `Betreff: ${description}\n\nLiebe Leser,\n\nDiese Woche tauchen wir tief in ein Thema ein, das jedes Marketing-Team betrifft: ${description.toLowerCase()}.`,
          x: isEn
            ? `${description} 🚀\n\nAI is changing content creation forever. Here's what you need to know 👇\n\n#AI #Content`
            : `${description} 🚀\n\nKI verändert Content-Erstellung für immer. Das müssen Sie wissen 👇\n\n#KI #Content`,
          medium: isEn
            ? `# ${description}\n\nA deep dive into the future of content marketing and why AI-powered solutions are becoming the new standard.`
            : `# ${description}\n\nEin Deep Dive in die Zukunft des Content Marketings und warum KI-gestützte Lösungen zum neuen Standard werden.`,
          pressemitteilung: isEn
            ? `PRESS RELEASE\n\n${description}\n\nVienna, ${new Date().toLocaleDateString('en-US')} — xchannels.ai announces a new milestone in AI-powered content creation.`
            : `PRESSEMITTEILUNG\n\n${description}\n\nWien, ${new Date().toLocaleDateString('de-DE')} — xchannels.ai kündigt einen neuen Meilenstein in KI-gestützter Content-Erstellung an.`,
          youtube: isEn
            ? `Video Title: ${description}\n\nIn this video, we explore how AI is revolutionizing content marketing for small and medium businesses.`
            : `Video-Titel: ${description}\n\nIn diesem Video zeigen wir, wie KI das Content Marketing für kleine und mittlere Unternehmen revolutioniert.`,
          facebook: isEn
            ? `${description}\n\nDiscover how AI can transform your content strategy. 🎯\n\n#marketing #ai #contentcreation`
            : `${description}\n\nEntdecken Sie, wie KI Ihre Content-Strategie transformieren kann. 🎯\n\n#marketing #ki #contentcreation`,
          pinterest: isEn
            ? `${description} | AI Content Marketing Tips`
            : `${description} | KI Content Marketing Tipps`,
        };

        const text = demoTexts[platformKey] || `[Demo] ${description}`;
        const headline = platform?.hasHeadline
          ? (isEn ? `${description} — A Deep Dive` : `${description} — Ein Deep Dive`)
          : null;

        return {
          id: `showcase-${Date.now()}-${idx}`,
          platform: platformKey,
          // Both keys so the existing JSX (which reads text_content + title)
          // renders the demo content correctly.
          text,
          text_content: text,
          headline,
          title: headline || (description.length > 60 ? `${description.slice(0, 60)}…` : description),
          status: 'draft',
          hashtags: platform?.hashtags > 0 ? ['#xchannels', '#contentmarketing', isEn ? '#ai' : '#ki'].slice(0, platform.hashtags) : [],
          content_number: Math.floor(Math.random() * 100) + 100,
          images: [],
          image_urls: [],
          created_at: new Date().toISOString(),
        };
      });

      setGeneratedContent(mockPieces);
      setContentCount(prev => prev + mockPieces.length);
      setDescription('');
      setSelectedPlatforms([]);
      setIsGenerating(false);
      toast.success(isEn
        ? `${mockPieces.length} content pieces created! (Demo)`
        : `${mockPieces.length} Content Pieces erstellt! (Demo)`);
    }, 2000);
  };

  const handleRefreshIdeas = async () => {
    setIsLoadingIdeas(true);
    setTimeout(() => {
      setIdeas(getRefreshIdeas(lang));
      setIsLoadingIdeas(false);
      toast.success(isEn ? 'New ideas loaded' : 'Neue Ideen geladen');
    }, 1200);
  };

  const [processingIdeaId, setProcessingIdeaId] = useState(null);
  const [savingIdeaId, setSavingIdeaId] = useState(null);

  const hasUploadedImages = files.some(f => f.type === 'image');

  const handleIdeaAction = async (ideaId, action, editedText = null) => {
    if (action === 'reject') {
      setIdeas(prev => prev.filter(i => i.id !== ideaId));
      toast.success(isEn ? 'Idea rejected' : 'Idee verworfen');
      return;
    }

    if (action === 'edit') {
      setSavingIdeaId(ideaId);
      setTimeout(() => {
        setIdeas(prev => prev.map(i => 
          i.id === ideaId ? { ...i, title: editedText?.split('\n')[0] || i.title, description: editedText || i.description } : i
        ));
        setEditingIdeaId(null);
        setSavingIdeaId(null);
        toast.success(isEn ? 'Idea updated' : 'Idee aktualisiert');
      }, 800);
      return;
    }

    // Accept — generate full content piece
    setProcessingIdeaId(ideaId);
    setTimeout(() => {
      const idea = ideas.find(i => i.id === ideaId);
      setIdeas(prev => prev.filter(i => i.id !== ideaId));
      if (idea) {
        const ideaLang = idea.language === 'en' ? 'en' : (lang === 'en' ? 'en' : 'de');
        const en = ideaLang === 'en';
        const demoTexts = {
          linkedin: en
            ? `${idea.title}\n\n${idea.description}\n\nThis is what modern content marketing looks like. AI-powered, multi-channel, on-brand.\n\n#contentmarketing #ai #marketing`
            : `${idea.title}\n\n${idea.description}\n\nSo sieht modernes Content Marketing aus. KI-gestützt, multi-channel, on-brand.\n\n#contentmarketing #ki #marketing`,
          instagram: en
            ? `${idea.title} ✨\n\n${idea.description}\n\n#contentcreation #aimarketing #socialmedia #digital #startup`
            : `${idea.title} ✨\n\n${idea.description}\n\n#contentcreation #kimarketing #socialmedia #digital #startup`,
          blog: en
            ? `# ${idea.title}\n\n${idea.description}\n\n## Why This Matters\n\nContent marketing has evolved from a nice-to-have to an absolute necessity.`
            : `# ${idea.title}\n\n${idea.description}\n\n## Warum das wichtig ist\n\nContent Marketing hat sich von einem Nice-to-have zu einer absoluten Notwendigkeit entwickelt.`,
          medium: en
            ? `# ${idea.title}\n\n${idea.description}\n\nA deep dive into the future of content marketing.`
            : `# ${idea.title}\n\n${idea.description}\n\nEin Deep Dive in die Zukunft des Content Marketings.`,
          newsletter: en
            ? `Subject: ${idea.title}\n\nDear Reader,\n\n${idea.description}`
            : `Betreff: ${idea.title}\n\nLiebe Leser,\n\n${idea.description}`,
          x: en
            ? `${idea.title} 🚀\n\n${idea.description}\n\n#AI #Content`
            : `${idea.title} 🚀\n\n${idea.description}\n\n#KI #Content`,
          pressemitteilung: en
            ? `PRESS RELEASE\n\n${idea.title}\n\n${idea.description}`
            : `PRESSEMITTEILUNG\n\n${idea.title}\n\n${idea.description}`,
          youtube: en
            ? `Video Title: ${idea.title}\n\n${idea.description}`
            : `Video-Titel: ${idea.title}\n\n${idea.description}`,
          facebook: en
            ? `${idea.title}\n\n${idea.description}\n\n#marketing #ai #contentcreation`
            : `${idea.title}\n\n${idea.description}\n\n#marketing #ki #contentcreation`,
          pinterest: en
            ? `${idea.title} | AI Content Marketing Tips`
            : `${idea.title} | KI Content Marketing Tipps`,
        };
        const platform = platforms.find(p => p.key === idea.platform);
        const text = demoTexts[idea.platform] || `${idea.title}\n\n${idea.description}`;
        setGeneratedContent([{
          id: `gen-${Date.now()}`,
          platform: idea.platform,
          text,
          text_content: text,
          headline: platform?.hasHeadline ? idea.title : null,
          title: idea.title,
          status: 'draft',
          content_number: Math.floor(Math.random() * 100) + 100,
          images: [],
          image_urls: [],
          created_at: new Date().toISOString(),
          hashtags: platform?.hashtags > 0
            ? ['#xchannels', '#contentmarketing', en ? '#ai' : '#ki'].slice(0, platform.hashtags)
            : [],
        }]);
      }
      setProcessingIdeaId(null);
      setContentCount(prev => prev + 1);
      toast.success(isEn ? 'Content generated! (Demo)' : 'Content generiert! (Demo)');
    }, 1500);
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getPlatformIcon = (platformKey) => {
    const platform = platforms.find(p => p.key === platformKey);
    if (platform) {
      const Icon = platform.icon;
      return <Icon className="w-4 h-4" style={{ color: platform.color }} />;
    }
    // Check custom channels
    const cc = customChannels.find(c => c.id === platformKey);
    if (cc) {
      return <Layers className="w-4 h-4" style={{ color: '#A0AEC0' }} />;
    }
    return null;
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-8" data-testid="kreation-page" onClick={() => { showLangDropdown && setShowLangDropdown(false); showElementDropdown && setShowElementDropdown(false); }}>
      {/* Left Panel - Input & Generation */}
      <div className="flex-1 lg:flex-[6]">
        {/* Header */}
        <div className="mb-4 lg:mb-6">
          <h1 className="text-2xl lg:text-4xl font-black tracking-tight text-white mb-1 lg:mb-2">
            {t.kreation.title}
          </h1>
          <p className="text-sm lg:text-base text-[#A0AEC0]">
            {t.kreation.subtitle}
          </p>
        </div>

        {/* Instagram Hashtag Update Banner */}
        {showInstagramBanner && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 lg:mb-6 relative bg-gradient-to-r from-[#E4405F]/15 to-[#E4405F]/5 border border-[#E4405F]/30 rounded-xl p-4 lg:p-5"
            data-testid="instagram-hashtag-banner"
          >
            <button
              onClick={() => {
                setShowInstagramBanner(false);
                localStorage.setItem('instagram_hashtag_banner_dismissed', 'true');
              }}
              className="absolute top-3 right-3 text-[#A0AEC0] hover:text-white transition-colors"
              data-testid="dismiss-instagram-banner"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E4405F]/20 flex items-center justify-center mt-0.5">
                <FaInstagram className="w-5 h-5 text-[#E4405F]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm lg:text-base mb-1">Instagram Update</h3>
                <p className="text-[#A0AEC0] text-xs lg:text-sm leading-relaxed">
                  Instagram erlaubt seit Ende 2025 <span className="text-[#E4405F] font-semibold">maximal 5 Hashtags</span> pro Post. 
                  Posts mit mehr Hashtags werden als Spam gewertet oder shadowbanned. 
                  Wir haben deshalb bei allen geplanten Instagram-Posts in der Distribution automatisch auf die 5 relevantesten Hashtags gekürzt. 
                  Du kannst die Hashtags jederzeit manuell in der Distribution bearbeiten.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Content Counter */}
        <div className="mb-4 lg:mb-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#1A2B42] to-[#0A1628] border border-[#00D9C8]/20 p-4 lg:p-5"
          >
            {/* Decorative background glow */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#00D9C8]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-5 -left-5 w-20 h-20 bg-[#00D9C8]/5 rounded-full blur-2xl" />
            
            <div className="relative flex items-center gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#00D9C8]/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#00D9C8]" />
              </div>
              
              {/* Text */}
              <div className="flex flex-col">
                <span className="text-[#5A6C7D] text-xs uppercase tracking-wider">
                  {t.kreation.togetherCreatedPre}
                </span>
                <span className="text-[#00D9C8] font-bold text-2xl leading-tight">
                  {contentCount} Content Pieces
                </span>
                <span className="text-[#5A6C7D] text-xs">
                  {t.kreation.togetherCreatedPost}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Input Section */}
        <Card className="bg-[#1A2B42] border-transparent mb-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-white mb-4">
              {t.kreation.newContentDesc}
            </h2>

            {/* Description Textarea with Attachment Button */}
            <div className="relative mb-4">
              <Textarea
                placeholder={t.kreation.describeTopic}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[120px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white placeholder:text-[#5A6C7D] resize-none pr-32"
                data-testid="content-description-input"
              />
              
              {/* Attachment Button - Bottom Right Corner */}
              <label 
                className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#00D9C8]/10 text-[#00D9C8] hover:bg-[#00D9C8]/20 border border-[#00D9C8]/30 rounded cursor-pointer transition-all"
                data-testid="file-attach-button"
              >
                <span>📎</span>
                <span>{t.kreation.uploadFiles}</span>
                <input
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.docx,.txt"
                  className="hidden"
                  onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                />
              </label>
            </div>
            
            {/* Uploaded Files */}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {files.map(file => (
                  <div 
                    key={file.id}
                    className="flex items-center gap-1.5 px-2 py-1 bg-[#0A1628] rounded text-xs"
                  >
                    {getFileIcon(file.type)}
                    <span className="text-white max-w-[100px] truncate">{file.name}</span>
                    <button 
                      onClick={() => removeFile(file.id)}
                      className="text-[#5A6C7D] hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Erweiterte Einstellungen */}
            <div className="mb-5" data-testid="advanced-settings">
              <p className="text-sm font-semibold text-white mb-3">{t.settings.preferences.title}</p>
              <div className="flex flex-wrap items-center gap-3">
                {/* Language Selector */}
                <div className="relative" data-testid="language-selector" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => { setShowLangDropdown(!showLangDropdown); setShowElementDropdown(false); }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#0A1628] border border-[#2D3F59] hover:border-[#00D9C8]/50 rounded text-sm transition-all"
                    data-testid="language-selector-btn"
                  >
                    <Globe className="w-3.5 h-3.5 text-[#00D9C8]" />
                    <span className="text-white font-medium">{LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.abbr || 'DE'}</span>
                  </button>
                  {showLangDropdown && (
                    <div className="absolute left-0 top-full mt-1 bg-[#1A2B42] border border-[#2D3F59] rounded shadow-lg z-50 min-w-[130px]">
                      {LANGUAGE_OPTIONS.map(lang => (
                        <button
                          key={lang.code}
                          onClick={() => { setSelectedLanguage(lang.code); setShowLangDropdown(false); }}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#0A1628] transition-colors ${selectedLanguage === lang.code ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}
                          data-testid={`lang-option-${lang.code}`}
                        >
                          <span>{lang.label}</span>
                          <span className="font-mono font-bold">{lang.abbr}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Element Selector */}
                <div className="relative" data-testid="element-selector" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => { setShowElementDropdown(!showElementDropdown); setShowLangDropdown(false); }}
                    className={`flex items-center gap-2 px-3 py-1.5 border rounded text-sm transition-all ${
                      selectedElement
                        ? 'bg-[#00D9C8]/10 border-[#00D9C8]/50 text-[#00D9C8]'
                        : 'bg-[#0A1628] border-[#2D3F59] hover:border-[#00D9C8]/50 text-[#A0AEC0]'
                    }`}
                    data-testid="element-selector-btn"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span className="font-medium">{selectedElement ? ELEMENT_OPTIONS.find(e => e.code === selectedElement)?.label : t.common.items}</span>
                  </button>
                  {showElementDropdown && (
                    <div className="absolute left-0 top-full mt-1 bg-[#1A2B42] border border-[#2D3F59] rounded shadow-lg z-50 min-w-[170px]">
                      <button
                        onClick={() => { setSelectedElement(null); setShowElementDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#0A1628] transition-colors ${!selectedElement ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}
                        data-testid="element-option-none"
                      >
                        <span>{isEn ? 'No element' : 'Kein Element'}</span>
                        {!selectedElement && <Check className="w-3.5 h-3.5" />}
                      </button>
                      {ELEMENT_OPTIONS.map(el => (
                        <button
                          key={el.code}
                          onClick={() => { setSelectedElement(el.code); setShowElementDropdown(false); }}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[#0A1628] transition-colors ${selectedElement === el.code ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}
                          data-testid={`element-option-${el.code}`}
                        >
                          <span>{el.label}</span>
                          {selectedElement === el.code && <Check className="w-3.5 h-3.5" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Eingabetext beibehalten Switch */}
                <label className="flex items-center gap-2 cursor-pointer" data-testid="keep-input-text-toggle">
                  <Switch
                    checked={keepInputText}
                    onCheckedChange={setKeepInputText}
                    className="data-[state=checked]:bg-[#00D9C8] data-[state=unchecked]:bg-[#3D4F65] border border-[#0A1628] shadow-sm"
                  />
                  <span className={`text-sm ${keepInputText ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}>{t.kreation.keepInputText}</span>
                </label>

                {/* Fotos beibehalten Switch - nur bei Bild-Upload */}
                {hasUploadedImages && (
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="keep-uploaded-photos-toggle">
                    <Switch
                      checked={keepUploadedPhotos}
                      onCheckedChange={setKeepUploadedPhotos}
                      className="data-[state=checked]:bg-[#00D9C8] data-[state=unchecked]:bg-[#3D4F65] border border-[#0A1628] shadow-sm"
                    />
                    <span className={`text-sm ${keepUploadedPhotos ? 'text-[#00D9C8]' : 'text-[#A0AEC0]'}`}>{t.kreation.keepPhotos}</span>
                  </label>
                )}
              </div>
            </div>

            {/* Platform Selector */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-white mb-3">{t.kreation.selectPlatforms}</p>
              <div className="grid grid-cols-3 lg:grid-cols-4 gap-1.5 lg:gap-2">
                {activePlatforms.map(platform => {
                  const Icon = platform.icon;
                  const isSelected = selectedPlatforms.includes(platform.key);
                  return (
                    <button
                      key={platform.key}
                      onClick={() => togglePlatform(platform.key)}
                      className={`flex flex-col lg:flex-row items-center gap-1 lg:gap-2 px-2 lg:px-3 py-2 transition-all ${
                        isSelected 
                          ? 'bg-[#00D9C8]/10 border border-[#00D9C8]' 
                          : 'bg-[#0A1628] border border-transparent hover:border-[#00D9C8]/50'
                      }`}
                      data-testid={`platform-${platform.key}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: isSelected ? platform.color : '#5A6C7D' }} />
                      <span className={`text-[10px] lg:text-xs text-center lg:text-left ${isSelected ? 'text-white' : 'text-[#A0AEC0]'}`}>
                        {t.platforms[platform.key] || platform.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-[#5A6C7D] mt-2">
                {selectedPlatforms.length}/{MAX_PLATFORMS} {t.kreation.platformsSelected.replace('{{max}}', MAX_PLATFORMS)}
              </p>
            </div>

            {/* Generate Button */}
            {/* Generate Button — kein Limit in der Showcase */}
            <Button 
              onClick={handleGenerate}
              disabled={isGenerating || !description.trim() || selectedPlatforms.length === 0}
              className="w-full h-10 lg:h-12 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold disabled:opacity-50 text-sm lg:text-base"
              data-testid="generate-content-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 mr-2 animate-spin" />
                  <span className="hidden sm:inline">{t.kreation.generating}</span>
                  <span className="sm:hidden">{t.common.generating}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                  {t.kreation.generateBtn}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Loading Skeletons */}
        {isGenerating && (
          <div className="space-y-4">
            {selectedPlatforms.map((platform, i) => (
              <motion.div
                key={platform}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#1A2B42] p-4 animate-pulse"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-[#0A1628]" />
                  <div className="h-4 bg-[#0A1628] w-24" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-[#0A1628] w-full" />
                  <div className="h-3 bg-[#0A1628] w-3/4" />
                  <div className="h-3 bg-[#0A1628] w-1/2" />
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Generated Content Preview - Shows during and after generation */}
        {generatedContent.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 text-[#00D9C8] animate-spin" />
                    {t.kreation.generating}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 text-[#00D9C8]" />
                    {generatedContent.filter(c => c.status !== 'generating' && c.status !== 'failed').length} Content Piece{generatedContent.filter(c => c.status !== 'generating' && c.status !== 'failed').length !== 1 ? 's' : ''} {t.common.success.toLowerCase()}
                  </>
                )}
              </h3>
              {!isGenerating && (
                <button
                  type="button"
                  onClick={() => onViewInProduction && onViewInProduction(generatedContent)}
                  className="text-[#00D9C8] text-sm hover:underline inline-flex items-center gap-1"
                  data-testid="view-all-in-production"
                >
                  {t.produktion.allContent}
                  <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>
            {generatedContent.map((content, index) => {
              const isGeneratingItem = content.status === 'generating';
              const isFailed = content.status === 'failed';
              const platformInfo = platforms.find(p => p.key === content.platform);
              const PlatformIcon = platformInfo?.icon;
              
              return (
                <motion.div
                  key={content.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={`overflow-hidden transition-all ${
                    isGeneratingItem 
                      ? 'bg-[#0A1628] border-[#2D3F59] opacity-70' 
                      : isFailed
                        ? 'bg-[#1A2B42] border-red-500/50'
                        : 'bg-[#1A2B42] border-transparent hover:border-[#00D9C8]'
                  }`}>
                    <div className="flex">
                      {/* Image Preview or Placeholder */}
                      <div className="w-24 h-24 flex-shrink-0 bg-[#0A1628] flex items-center justify-center">
                        {content.image_urls?.length > 0 ? (
                          <img 
                            src={content.image_urls[0]} 
                            alt={content.title}
                            className="w-full h-full object-cover"
                          />
                        ) : isGeneratingItem ? (
                          <Loader2 className="w-8 h-8 text-[#5A6C7D] animate-spin" />
                        ) : PlatformIcon ? (
                          <PlatformIcon className="w-10 h-10" style={{ color: platformInfo?.color || '#5A6C7D' }} />
                        ) : (
                          <FileText className="w-10 h-10 text-[#5A6C7D]" />
                        )}
                      </div>
                      <CardContent className="p-3 flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {PlatformIcon && (
                              <PlatformIcon 
                                className="w-4 h-4" 
                                style={{ color: isGeneratingItem ? '#5A6C7D' : platformInfo?.color }} 
                              />
                            )}
                            <Badge className={`text-xs ${
                              isGeneratingItem 
                                ? 'bg-[#2D3F59] text-[#5A6C7D] border-[#5A6C7D]'
                                : 'bg-[#0A1628] text-[#00D9C8] border-[#00D9C8]'
                            }`}>
                              {platformInfo?.label || content.platform}
                            </Badge>
                            {content.content_number && (
                              <span className="text-xs text-[#5A6C7D]">#{content.content_number}</span>
                            )}
                          </div>
                          <Badge className={`text-xs ${
                            isGeneratingItem 
                              ? 'bg-yellow-500/20 text-yellow-500'
                              : isFailed
                                ? 'bg-red-500/20 text-red-500'
                                : 'bg-green-500/20 text-green-500'
                          }`}>
                            {isGeneratingItem ? t.common.generating : isFailed ? t.common.failed : t.common.draft}
                          </Badge>
                        </div>
                        
                        {isGeneratingItem ? (
                          <div className="space-y-2">
                            <div className="h-3 bg-[#2D3F59] rounded animate-pulse w-3/4"></div>
                            <div className="h-2 bg-[#2D3F59] rounded animate-pulse w-full"></div>
                            <div className="h-2 bg-[#2D3F59] rounded animate-pulse w-2/3"></div>
                          </div>
                        ) : (
                          <>
                            <h4 className="text-sm font-medium text-white mb-1 line-clamp-1">{content.title}</h4>
                            <p className="text-xs text-[#A0AEC0] line-clamp-2 mb-2">
                              {content.text_content}
                            </p>
                            {!isFailed && (
                              <button
                                type="button"
                                onClick={() => onViewInProduction && onViewInProduction([content])}
                                className="text-[#00D9C8] text-xs hover:underline inline-flex items-center gap-1"
                                data-testid={`view-content-${content.id}`}
                              >
                                {isEn ? 'View in Production' : 'In Produktion'}
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            )}
                          </>
                        )}
                      </CardContent>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right Panel - Ideas (40%) - Shows below on mobile */}
      <div className="flex-none lg:flex-[4] mt-6 lg:mt-0">
        <div className="lg:sticky lg:top-0">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h2 className="text-lg lg:text-xl font-bold text-white">{t.kreation.contentIdeas}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshIdeas}
              disabled={isLoadingIdeas}
              className="text-[#A0AEC0] hover:text-white hover:bg-[#1A2B42]"
              data-testid="refresh-ideas-btn"
            >
              <RefreshCw className={`w-4 h-4 ${isLoadingIdeas ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Ideas Loading */}
          {isLoadingIdeas && ideas.length === 0 && (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-[#1A2B42] p-3 lg:p-4 animate-pulse">
                  <div className="h-4 bg-[#0A1628] w-3/4 mb-2" />
                  <div className="h-3 bg-[#0A1628] w-full mb-1" />
                  <div className="h-3 bg-[#0A1628] w-2/3" />
                </div>
              ))}
            </div>
          )}

          {/* Ideas List */}
          <AnimatePresence>
            {ideas.map((idea, index) => {
              // Ensure platform always has a value
              const ideaPlatform = idea.platform || 'linkedin';
              const platformConfig = activePlatforms.find(p => p.key === ideaPlatform) || platforms.find(p => p.key === ideaPlatform);
              
              return (
              <motion.div
                key={idea.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20, height: 0 }}
                transition={{ delay: index * 0.05 }}
                className="mb-2 lg:mb-3"
              >
                <Card className="bg-[#1A2B42] border-transparent">
                  <CardContent className="p-3 lg:p-4">
                    {editingIdeaId === idea.id ? (
                      /* Edit Mode */
                      <div>
                        <div className="flex items-center gap-2 mb-2 lg:mb-3">
                          {getPlatformIcon(ideaPlatform)}
                          <Badge className="bg-[#0A1628] text-[#00D9C8] border-[#00D9C8] text-xs">
                            {platformConfig?.label || ideaPlatform}
                          </Badge>
                          <Badge 
                            className="bg-[#0A1628] text-white border-[#5A6C7D] text-[10px] font-mono cursor-pointer hover:border-[#00D9C8] transition-colors"
                            onClick={() => {
                              const currentLang = idea.language || selectedLanguage;
                              const langCodes = LANGUAGE_OPTIONS.map(l => l.code);
                              const idx = langCodes.indexOf(currentLang);
                              const nextLang = langCodes[(idx + 1) % langCodes.length];
                              setIdeas(prev => prev.map(i => i.id === idea.id ? { ...i, language: nextLang } : i));
                            }}
                          >
                            {(idea.language || selectedLanguage || 'de').toUpperCase()}
                          </Badge>
                        </div>
                        <Textarea
                          value={editedIdeaText}
                          onChange={(e) => setEditedIdeaText(e.target.value)}
                          className="min-h-[80px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm mb-2 lg:mb-3"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleIdeaAction(idea.id, 'edit', editedIdeaText)}
                            className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] text-xs lg:text-sm"
                            disabled={savingIdeaId === idea.id}
                          >
                            {savingIdeaId === idea.id ? (
                              <>
                                <Loader2 className="w-3 h-3 lg:w-4 lg:h-4 mr-1 animate-spin" />
                                <span className="hidden sm:inline">{isEn ? 'Generating…' : 'Generiere...'}</span>
                              </>
                            ) : (
                              <>
                                <Check className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                                {t.common.save}
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingIdeaId(null)}
                            className="text-[#A0AEC0] text-xs lg:text-sm"
                            disabled={savingIdeaId === idea.id}
                          >
                            {t.common.cancel}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <>
                        <div className="flex items-center gap-1.5 lg:gap-2 mb-1.5 lg:mb-2 flex-wrap">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex items-center gap-1.5 bg-[#0A1628] text-[#00D9C8] border border-[#00D9C8] rounded-full px-2 py-0.5 text-[10px] lg:text-xs hover:bg-[#00D9C8]/10 transition-colors cursor-pointer" data-testid={`idea-platform-dropdown-${idea.id}`}>
                                {getPlatformIcon(ideaPlatform)}
                                <span>{platformConfig?.label || ideaPlatform}</span>
                                <ChevronDown className="w-3 h-3 ml-0.5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#1A2B42] border-[#0A1628]">
                              {activePlatforms.map(p => (
                                <DropdownMenuItem
                                  key={p.key}
                                  onClick={() => setIdeas(prev => prev.map(i => i.id === idea.id ? { ...i, platform: p.key } : i))}
                                  className="text-white hover:bg-[#0A1628] text-xs"
                                >
                                  {getPlatformIcon(p.key)}
                                  <span className="ml-2">{p.label}</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Badge 
                            className="bg-[#0A1628] text-white border-[#5A6C7D] text-[10px] lg:text-xs font-mono cursor-pointer hover:border-[#00D9C8] transition-colors"
                            data-testid={`idea-lang-${idea.id}`}
                            onClick={() => {
                              // Toggle language on the idea
                              const currentLang = idea.language || selectedLanguage;
                              const nextLang = (() => {
                                const langCodes = LANGUAGE_OPTIONS.map(l => l.code);
                                const idx = langCodes.indexOf(currentLang);
                                return langCodes[(idx + 1) % langCodes.length];
                              })();
                              setIdeas(prev => prev.map(i => i.id === idea.id ? { ...i, language: nextLang } : i));
                            }}
                          >
                            {(idea.language || selectedLanguage || 'de').toUpperCase()}
                          </Badge>
                          {selectedElement && (
                            <Badge className="bg-[#00D9C8]/10 text-[#00D9C8] border-[#00D9C8]/50 text-[10px] lg:text-xs" data-testid={`idea-element-${idea.id}`}>
                              {ELEMENT_OPTIONS.find(e => e.code === selectedElement)?.label}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-[10px] lg:text-xs border-[#5A6C7D] text-[#A0AEC0]">
                            {idea.goal}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-white mb-1 text-sm lg:text-base">{idea.title}</h4>
                        <p className="text-xs lg:text-sm text-[#A0AEC0] line-clamp-2 mb-2 lg:mb-3">
                          {idea.description}
                        </p>
                        {processingIdeaId === idea.id ? (
                          <div className="flex items-center gap-2 text-[#00D9C8]">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">{isEn ? 'Generating content…' : 'Content wird generiert...'}</span>
                          </div>
                        ) : (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleIdeaAction(idea.id, 'accept')}
                            className="text-[#00D9C8] hover:bg-[#00D9C8]/10"
                            title={t.kreation.generateBtn}
                            data-testid={`accept-idea-${idea.id}`}
                            disabled={processingIdeaId !== null || savingIdeaId !== null}
                          >
                            <Heart className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingIdeaId(idea.id);
                              setEditedIdeaText(`${idea.title}\n\n${idea.description}`);
                            }}
                            className="text-[#A0AEC0] hover:text-white hover:bg-[#0A1628]"
                            title={t.kreation.editBtn}
                            data-testid={`edit-idea-${idea.id}`}
                            disabled={processingIdeaId !== null || savingIdeaId !== null}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleIdeaAction(idea.id, 'reject')}
                            className="text-[#A0AEC0] hover:text-red-400 hover:bg-red-400/10"
                            title={t.kreation.rejectBtn}
                            data-testid={`reject-idea-${idea.id}`}
                            disabled={processingIdeaId !== null || savingIdeaId !== null}
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                        </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
            })}
          </AnimatePresence>

          {/* Empty Ideas State */}
          {ideas.length === 0 && !isLoadingIdeas && (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#5A6C7D]" />
              <p className="text-[#A0AEC0] text-sm mb-3">{t.common.noResults}</p>
              <Button
                onClick={handleRefreshIdeas}
                className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628]"
                data-testid="load-new-ideas-btn"
              >
                {t.kreation.refreshIdeas}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowcaseKreation;
